import { ChangeDetectionStrategy, Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsGraphicObject,
  HsMatrix4,
  HsQuaternion,
  HsShaderData,
  HsShaderObjectType,
  HsTextureMaterial,
  HsTextureUtils,
  HsVector3,
  hsMakeTexturedIndexedGeometry,
  type HsDefaultShader,
  type HsHypersceneViewer,
  type HsTexturedIndexedGeometry,
  type HsViewerInputOptions,
} from '@sibe/hyperscene';
import { Subscription, timer } from 'rxjs';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import { makeTexturedCubeGeometryData } from './textured-cube-geometry-data.function';

enum ObjectType {
  IndexedMesh = 'Indexed mesh',
  StandardMesh = 'Standard mesh',
  IndexedMeshCollection = 'Indexed mesh collection',
  StandardMeshCollection = 'Standard mesh collection',
}

/** Objects of one geometry object type, moved together by `matrix`. */
interface ObjectGroup {
  readonly matrix: HsMatrix4;
  readonly objects: HsGraphicObject[];
  readonly offsets: HsMatrix4[];
}

const IMAGE_URLS: string[] = [
  '/assets/img/test-img-1.png',
  '/assets/img/test-img-2.png',
  '/assets/img/test-img-3.png',
  '/assets/img/test-img-4.png',
  '/assets/img/test-img-5.png',
];

const ROTATION: HsMatrix4 = HsMatrix4.compose(
  [0, 0, 0],
  HsQuaternion.makeFromAxisAndAngle(new HsVector3(1, 0.5, 0.25).normalize().values, 0.01).values,
  [1, 1, 1]
);

const COLLECTION_SIZE: number = 8;

/** A rotating textured cube or block of cubes; the texture changes every five seconds. */
@Component({
  selector: 'textured-geometric-objects',
  templateUrl: './textured-geometric-objects.component.html',
  styleUrls: ['./textured-geometric-objects.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TexturedGeometricObjects extends DemoViewerPage implements OnDestroy {
  public readonly objectTypes: ObjectType[] = [
    ObjectType.IndexedMesh,
    ObjectType.StandardMesh,
    ObjectType.IndexedMeshCollection,
    ObjectType.StandardMeshCollection,
  ];

  public selectedObjectType: ObjectType = ObjectType.IndexedMesh;

  private readonly groups: Map<ObjectType, ObjectGroup> = new Map<ObjectType, ObjectGroup>();
  private readonly textures: WebGLTexture[] = [];
  private readonly subscription: Subscription = new Subscription();
  private material: HsTextureMaterial<HsDefaultShader> | null = null;
  private textureIndex: number = -1;

  protected override inputOptions(): HsViewerInputOptions | null {
    return null;
  }

  protected initScene(viewer: HsHypersceneViewer): void {
    const indexedGeometry: HsTexturedIndexedGeometry | null = hsMakeTexturedIndexedGeometry(
      viewer.surface.graphics,
      makeTexturedCubeGeometryData(true)
    );
    // The current engine draws textured meshes only from indexed geometry; this one keeps every triangle corner apart.
    const standardGeometry: HsTexturedIndexedGeometry | null = hsMakeTexturedIndexedGeometry(
      viewer.surface.graphics,
      makeTexturedCubeGeometryData(false)
    );
    if (indexedGeometry === null || standardGeometry === null) {
      return;
    }
    const material: HsTextureMaterial<HsDefaultShader> = this.makeMaterial(viewer.gl);
    this.material = material;

    this.groups.set(ObjectType.IndexedMesh, this.makeSingleGroup(indexedGeometry, material));
    this.groups.set(ObjectType.StandardMesh, this.makeSingleGroup(standardGeometry, material));
    this.groups.set(ObjectType.IndexedMeshCollection, this.makeCollectionGroup(indexedGeometry, material));
    this.groups.set(ObjectType.StandardMeshCollection, this.makeCollectionGroup(standardGeometry, material));
    this.selectObject(this.selectedObjectType);
    viewer.camera.worldMatrix.setPosition([0, 0, 5]);

    IMAGE_URLS.forEach((url: string) => {
      void HsTextureUtils.load(viewer.gl, url).then((texture: WebGLTexture | null) => {
        if (texture !== null && this.viewer !== null) {
          this.textures.push(texture);
        }
      });
    });
    this.subscription.add(timer(1000, 5000).subscribe(() => this.applyNextTexture()));
  }

  protected override animate(): boolean {
    const group: ObjectGroup | undefined = this.groups.get(this.selectedObjectType);
    if (group === undefined) {
      return false;
    }
    group.matrix.multiply(ROTATION);
    group.objects.forEach((object: HsGraphicObject, index: number) => {
      object.worldMatrix.multiplyMatrices(group.matrix, group.offsets[index]);
      object.updateBoundingSphere();
    });
    return true;
  }

  public override ngOnDestroy(): void {
    this.subscription.unsubscribe();
    super.ngOnDestroy();
  }

  public selectObject(objectType: ObjectType): void {
    this.selectedObjectType = objectType;
    if (this.viewer === null) {
      return;
    }
    const viewer: HsHypersceneViewer = this.viewer;
    this.groups.forEach((group: ObjectGroup, type: ObjectType) => {
      group.objects.forEach((object: HsGraphicObject) => {
        if (type === objectType) {
          viewer.add(object);
        } else {
          viewer.queue.removeObject(object);
        }
      });
    });
    viewer.invalidate();
  }

  /** A lit texture material that starts with a plain grey texture until the images load. */
  private makeMaterial(gl: WebGL2RenderingContext): HsTextureMaterial<HsDefaultShader> {
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    shaderData.addColor().addDepth().addNormals().addDiffuseLighting().addTexture();
    const material: HsTextureMaterial<HsDefaultShader> = new HsTextureMaterial(gl, shaderData.makeShader());
    material.color.set(1, 1, 1);
    const texture: WebGLTexture | null = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([200, 200, 200, 255]));
    gl.bindTexture(gl.TEXTURE_2D, null);
    material.texture = texture;
    return material;
  }

  private makeSingleGroup(
    geometry: HsTexturedIndexedGeometry,
    material: HsTextureMaterial<HsDefaultShader>
  ): ObjectGroup {
    return {
      matrix: HsMatrix4.makeIdentityMatrix(),
      objects: [new HsGraphicObject(getUuid(), 'cube', geometry, material)],
      offsets: [HsMatrix4.makeIdentityMatrix()],
    };
  }

  /** An 8x8x8 block of cubes 50 units in front of the camera, rotating around its center. */
  private makeCollectionGroup(
    geometry: HsTexturedIndexedGeometry,
    material: HsTextureMaterial<HsDefaultShader>
  ): ObjectGroup {
    const group: ObjectGroup = { matrix: HsMatrix4.makeIdentityMatrix(), objects: [], offsets: [] };
    group.matrix.setPosition([0, 0, -50]);
    const center: number = (COLLECTION_SIZE * 3) / 2;
    for (let i: number = 0; i < COLLECTION_SIZE; i++) {
      for (let j: number = 0; j < COLLECTION_SIZE; j++) {
        for (let k: number = 0; k < COLLECTION_SIZE; k++) {
          group.objects.push(new HsGraphicObject(getUuid(), 'cube', geometry, material));
          group.offsets.push(
            HsMatrix4.makeIdentityMatrix().setPosition([i * 3 - center, j * 3 - center, k * 3 - center])
          );
        }
      }
    }
    return group;
  }

  private applyNextTexture(): void {
    if (this.material === null || this.textures.length === 0) {
      return;
    }
    this.textureIndex = (this.textureIndex + 1) % this.textures.length;
    this.material.texture = this.textures[this.textureIndex];
    this.viewer?.requestRender();
  }
}
